/**
 * Ask before the page takes a live call down with it.
 *
 * Cmd+W, Cmd+R and a stray click on a link all end a call the same way: the tab
 * goes, the Voice SDK goes with it, and the person on the other end hears the
 * line die mid-sentence. There is nothing to recover afterwards - nothing in
 * this app re-joins a call across a reload - so the only place to intervene is
 * before the page is allowed to leave.
 *
 * The browser's own prompt is the whole mechanism, and it is crude on purpose:
 * the wording has been the browser's since 2016 and no page may choose it. That
 * is fine here. The question is not what it says, it is that it is asked at all.
 *
 * WHY THIS IS REGISTERED CONDITIONALLY, and not once for the session. A live
 * `beforeunload` listener makes a page ineligible for the back/forward cache in
 * Firefox, and this app leans on the frozen-page path in two places that matter:
 * `device/useDeviceLease.ts` and `device/useCallPresence.ts` both release on
 * `pagehide`, because a mobile tab is frozen rather than closed. Registered for
 * the whole session, the listener would quietly change how every idle CRM window
 * suspends. Registered only while a call is up, the exposure lasts as long as
 * the conversation does.
 *
 * ⚠ NOT A GUARANTEE. Chrome only shows the dialog when the page has sticky
 * activation, so a window that rang, was answered from somewhere else and was
 * never clicked in can still be closed in silence. This narrows the hole rather
 * than closing it.
 */
import { useEffect } from "react";

/**
 * Warn on unload while `active` is true.
 *
 * The caller decides what "active" means - see `isLiveCall` in `callState.ts`,
 * which is the definition every reader in this folder shares. Passing the
 * provider's `busy` predicate instead would be a bug worth naming: that one
 * covers `ended` and `error` on purpose (so the Device is not yanked out from
 * under the after-call screen), and a prompt on every finished call is how a
 * guard gets ignored and then switched off.
 */
export function useUnloadWarning(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const warn = (e: BeforeUnloadEvent) => {
      // preventDefault is the modern signal. The assignment below is the old
      // one, still required by Safari; the string itself is discarded
      // everywhere, which is why it is empty rather than a sentence somebody
      // might try to translate.
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [active]);
}
